import { useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { MediaImageStandard, Typography, LinkButton } from 'components/Contentful'
import { BLOCKS } from '@contentful/rich-text-types'
import { Carousel } from 'components/Carousels'
import classNames from 'classnames'
import { useGraduate } from 'providers/GraduateProvider'
import Image from 'next/image'
const trustMarkersTypes = {
  trustpilot: { key: 'trustpilot', position: 'top-0 right-0 sm:top-[1%] sm:right-2' },
  bestCompany: { key: 'bestCompany', position: 'top-[20%] left-7' },
  consumerAffairs: {
    key: 'consumerAffairs',
    position: 'left-[20%] bottom-[5%] sm:bottom-[12%] sm:left-[15%]',
  },
  google: { key: 'google', position: 'bottom-[30%] right-2 sm:bottom-[35%] sm:right-4' },
}

const ItemComponentCarousel = ({ item, index }) => {
  return (
    <div>
      <div className="mt-2 flex items-center justify-center relative">
        <div>
          <MediaImageStandard
            content={item?.image}
            layout="fixed"
            className="w-full h-auto max-w-md lg:max-w-xl"
            height={500}
            width={629}
            alt={item?.image?.title}
            unoptimized={true}
            priority={index === 0}
            quality={100}
          />
          {item?.config?.bubbles?.map((bubble, index) => (
            <Typography
              key={index}
              className={classNames(
                'whitespace-nowrap py-1 px-2 lg:py-2 lg:px-3 absolute',
                bubble?.style
              )}
              content={bubble?.text}
            />
          ))}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-center">
        <div className="max-w-md lg:pb-7">
          {item?.disclaimer && <Typography content={item?.disclaimer} variant="bodyXs" />}
        </div>
      </div>
    </div>
  )
}

const TrustpilotCard = ({ trustMarker, type }) => {
  const { totalRating = 4.6, totalReviews = 1000 } = trustMarker || {}
  const getImage = () => {
    switch (type?.key) {
      case trustMarkersTypes.trustpilot.key:
        return '/multi-site-assets/trustpilot.svg'
      case trustMarkersTypes.bestCompany.key:
        return '/multi-site-assets/best-company.svg'
      case trustMarkersTypes.consumerAffairs.key:
        return '/multi-site-assets/consumer-affairs.svg'
      case trustMarkersTypes.google.key:
        return '/multi-site-assets/google.svg'
      default:
        return '/multi-site-assets/trustpilot.svg'
    }
  }
  return (
    <div
      className={`w-[100px] h-auto sm:w-[164px] sm:h-[105px] py-2 bg-[#f0f4fc] rounded-md border-[1px] border-solid border-[#154199] text-center absolute ${type?.position} z-10`}
    >
      <div className="flex items-center justify-center space-x-2">
        <Image
          src={getImage()}
          className="w-full h-[15px] sm:h-[21px]"
          height={24}
          width={140}
          alt={type?.key}
        />
      </div>
      <div className="flex justify-center items-center space-x-1 mb-1">
        {Array.from({ length: totalRating }).map((_, i) => (
          <svg key={i} className="w-3 h-3 sm:w-6 sm:h-6 fill-[#00b67a]" viewBox="0 0 24 24">
            <path d="M12 .587l3.668 7.431L24 9.587l-6 5.843L19.335 24 12 19.771 4.665 24 6 15.43 0 9.587l8.332-1.569z" />
          </svg>
        ))}
        <svg className="w-3 h-4 sm:w-6 sm:h-6 fill-[#00b67a]" viewBox="0 0 24 24">
          <defs>
            <linearGradient id="half">
              <stop offset="50%" stopColor="#00b67a" />
              <stop offset="50%" stopColor="#dcdcdc" />
            </linearGradient>
          </defs>
          <path
            d="M12 .587l3.668 7.431L24 9.587l-6 5.843L19.335 24 12 19.771 4.665 24 6 15.43 0 9.587l8.332-1.569z"
            fill="url(#half)"
          />
        </svg>
      </div>
      <div className="sm:text-sm text-[8px] text-gray-800 font-medium">
        {totalRating}/5 • <span className="font-semibold">{totalReviews}+</span> reviews
      </div>
    </div>
  )
}

function HeroSliderModule({
  grid,
  trustPilot,
  title,
  subTitle,
  graduateTitle,
  graduateSubTitle,
  eyebrow,
  config,
  button,
  graduateButton,
  buttonLink,
  trustMarkersGrid,
}) {
  const { graduate } = useGraduate()
  const items = grid?.gridItemsCollection?.items || []
  const backgroundConfig = config?.jsonContent?.sectionBackground ?? 'bgDefault'
  const finalTitle = graduate && graduateTitle ? graduateTitle : title
  const finalSubtitle = graduate && graduateSubTitle ? graduateSubTitle : subTitle
  const variant = config?.jsonContent?.variant ?? 'default'
  const trustMarkers =
    trustMarkersGrid?.gridItemsCollection?.items[0]?.gridItemsCollection?.items || []

  return (
    <Section variant={backgroundConfig}>
      <div className={classNames("flex flex-col lg:flex-row items-center justify-between p-0 md:p-4 pt-12 lg:pt-4 lg:px-0", { "mt-0 lg:mt-4": trustMarkers.length > 0 })}>
        <div
          className={
            variant === 'xl' ? 'max-w-[580px] xl:min-w-[580px]' : 'max-w-[520px] xl:min-w-[520px]'
          }
        >
          {eyebrow?.textContent && (
            <Typography
              content={eyebrow?.textContent}
              variant="labelSm"
              color="gray"
              fontWeight="bold"
              className="uppercase pb-4 tracking-eyebrow  text-center md:text-left"
            />
          )}
          {finalTitle?.textContent && (
            <Typography
              content={finalTitle?.textContent}
              variant="displayXl"
              mobileVariant="displayLg"
              responsive={true}
              className="pb-4 md:text-[68px]  text-center md:text-left"
            />
          )}
          {finalSubtitle?.textContent && (
            <Typography
              content={finalSubtitle?.textContent}
              variant="bodyLg"
              mobileVariant="labelMd"
              responsive={true}
              className="pb-4 !font-normal text-center md:text-left"
              fontWeight="regular"
            />
          )}

          {button && (
            <LinkButton
              track={{
                ...{
                  list_name: 'Hero Slider CTA click',
                  click_id: button?.linkText?.json?.content?.[0]?.content[0]?.value,
                  click_text: button?.linkText?.json?.content?.[0]?.content[0]?.value,
                  click_url: button?.linkHref,
                  track_event: 'internal_campaign_click',
                  event_action: 'button_click',
                  event_type: 'button_click',
                },
                ...button.event,
              }}
              typographicOptions={{
                variantOverride: { [BLOCKS.PARAGRAPH]: 'bodyLg' },
                fontWeight: 'bold',
              }}
              variant="contained"
              color="primary"
              className="shadow-md w-full lg:w-auto mt-2"
              content={button}
            />
          )}

          {buttonLink && (
            <LinkButton
              href={buttonLink?.linkHref}
              passHref
              track={{
                list_name: `FDR-Web | ${buttonLink?.text}`,
                click_id: buttonLink?.linkText?.json?.content?.[0]?.content[0]?.value,
                click_text: `FDR-Web | ${buttonLink?.text}`,
                click_url: buttonLink?.linkHref,
                track_event: 'internal_campaign_click',
                event_action: 'button_click',
                event_type: 'button_click',
                nav_link_section: 'Hero Card',
                page_section: 'Our clients say it best',
                event_name: 'FDR-Web | Reviews - Continue Clicked',
              }}
              noLink={false}
              withNextLink
              className="w-full lg:w-fit mt-0 lg:mt-4 bg-[#CB000E] hover:bg-[#1A2D54] px-6 py-3 rounded-md"
              content={buttonLink}
            />
          )}
          {graduateButton && graduate && (
            <LinkButton
              track={{
                ...{
                  list_name: 'Hero Slider Graduate CTA click',
                  click_id: graduateButton?.linkText?.json?.content?.[0]?.content[0]?.value,
                  click_text: graduateButton?.linkText?.json?.content?.[0]?.content[0]?.value,
                  click_url: graduateButton?.linkHref,
                  track_event: 'internal_campaign_click',
                  event_action: 'button_click',
                  event_type: 'button_click',
                },
                ...graduateButton.event,
              }}
              typographicOptions={{
                variantOverride: { [BLOCKS.PARAGRAPH]: 'bodyLg' },
                fontWeight: 'bold',
              }}
              variant="contained"
              color="primary"
              className="w-full lg:w-fit mt-0 lg:mt-4 bg-[#CB000E] hover:bg-[#1A2D54] hover:text-white px-6 py-3 rounded-md"
              content={graduateButton}
            />
          )}
          {trustPilot && (
            <div className="mt-7 flex items-center justify-center lg:justify-start">
              <MediaImageStandard
                content={trustPilot?.mediaContent}
                layout="fixed"
                alt={trustPilot?.mediaAltText}
                unoptimized={true}
                priority={true}
                quality={100}
              />
            </div>
          )}
        </div>
        {trustMarkers.length ? (
          <div className={`relative sm:mt-2 mr-8 mt-12 sm:mr-0`}>
            {trustMarkers.map((marker,i) => {
              return (
                <TrustpilotCard
                  key={'marker_' + i}
                  type={trustMarkersTypes[marker?.jsonContent?.company]}
                  trustMarker={marker?.jsonContent}
                />
              )
            })}
            <div className="relative">
              <Carousel
                autoPlay={true}
                carouselItems={items}
                itemComponent={ItemComponentCarousel}
                variant="preview-full"
                withIndicators={items?.length > 1}
                disableArrows={true}
                classNameIndicatorDots="flex bg-[#3300ff] mt-8 mb-8 lg:hidden hover:cursor-pointer"
                idPrefix="hero-slider"
                interval={10000}
                navigateOnClick={true}
                scrollOnChange={false}
                classNameContainer="max-w-[509px] lg:max-w-none "
                className="p-0"
              />
            </div>
          </div>
        ) : (
          <div className="relative my-4 ">
            <Carousel
              autoPlay={true}
              carouselItems={items}
              itemComponent={ItemComponentCarousel}
              variant="preview-full"
              withIndicators={items?.length > 1}
              disableArrows={true}
              classNameIndicatorDots="flex bg-[#3300ff] mt-8 mb-8 lg:hidden hover:cursor-pointer"
              idPrefix="hero-slider"
              interval={10000}
              navigateOnClick={true}
              scrollOnChange={false}
              classNameContainer="max-w-[509px] lg:max-w-none "
              className="p-0 sm:pl-4"
            />
          </div>
        )}
      </div>
    </Section>
  )
}

function HeroSlider({ content }) {
  const {
    grid,
    trustPilot,
    title,
    subTitle,
    graduateTitle,
    graduateSubTitle,
    eyebrow,
    config,
    button,
    graduateButton,
    buttonLink,
    trustMarkersGrid,
  } = useMemoizedContentGetter(content, [
    'grid',
    'trustPilot',
    'title',
    'subTitle',
    'graduateTitle',
    'graduateSubTitle',
    'eyebrow',
    'config',
    'button',
    'graduateButton',
    'buttonLink',
    'trustMarkersGrid',
  ])
  return (
    <HeroSliderModule
      title={title}
      subTitle={subTitle}
      eyebrow={eyebrow}
      grid={grid}
      trustPilot={trustPilot}
      config={config}
      button={button}
      buttonLink={buttonLink}
      graduateTitle={graduateTitle}
      graduateSubTitle={graduateSubTitle}
      graduateButton={graduateButton}
      trustMarkersGrid={trustMarkersGrid}
    />
  )
}

export { HeroSlider, HeroSliderModule }
export default HeroSlider
